import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import useTwitchStatus from '../hooks/useTwitchStatus';
import BrandIcon from '../components/BrandIcon';
import {
  RELEASE_DATE_LABEL,
  getTimeLeft,
  formatCountdownUnit,
} from '../utils/launchCountdown';

const STEAM_APP_ID = '3316570';
const TRAILER_VIDEO_ID = '0EhKlfpwsU8';
const STEAM_STORE_BASE = `https://store.steampowered.com/app/${STEAM_APP_ID}/Typing_Stars/`;
const STEAM_UTM = {
  utm_source: 'fxbgames',
  utm_medium: 'website',
  utm_campaign: 'typing_stars',
};

function steamStoreUrl(content) {
  const params = new URLSearchParams({ ...STEAM_UTM, utm_content: content });
  return `${STEAM_STORE_BASE}?${params.toString()}`;
}

function steamWidgetUrl(content = 'wishlist_band') {
  const params = new URLSearchParams({ ...STEAM_UTM, utm_content: content });
  return `https://store.steampowered.com/widget/${STEAM_APP_ID}/?${params.toString()}`;
}

const TWITCH_CHANNEL = 'itsflannelbeard';
const TWITCH_URL = `https://www.twitch.tv/${TWITCH_CHANNEL}`;
const CONTACT_EMAIL = 'hi@fxb.gg';

// Steam launch: August 24, 2026 at 10am PDT / 1pm EDT (17:00 UTC).

// Loops.so mailing list signup (public form endpoint — no API key needed).
const LOOPS_FORM_ENDPOINT = 'https://app.loops.so/api/newsletter-form/cmrsepyfo3i210j0rl7v0sv9b';
const LOOPS_MAILING_LIST_ID = 'cmrsfuxlu03230j2n1u6ee863';
const LOOPS_USER_GROUP = 'typing-stars';

const HERO_IMAGE = '/images/typing-stars/hero.jpg';
const SCREENSHOTS = [
  { src: '/images/typing-stars/ss1.jpg', alt: 'Typing Stars title screen on a retro CRT terminal' },
  { src: '/images/typing-stars/ss2.jpg', alt: 'Typing Stars gameplay screenshot' },
  { src: '/images/typing-stars/ss3.jpg', alt: 'Typing Stars gameplay screenshot' },
  { src: '/images/typing-stars/ss4.jpg', alt: 'Typing Stars gameplay screenshot' },
];

const PRESS = {
  quote:
    "Typing Stars is meant to be a relaxing 'straightforward game that helps you master typing speed and accuracy,' but it just completely stresses me out",
  source: 'PC Gamer',
  url: 'https://www.pcgamer.com/games/typing-stars-is-meant-to-be-a-relaxing-straightforward-game-that-helps-you-master-typing-speed-and-accuracy-but-it-just-completely-stresses-me-out/',
};

// Social links. Entries with a null href are hidden until a real URL is
// dropped in — replace the nulls below and they'll appear automatically.
const SOCIAL_LINKS = [
  { label: 'Twitch', icon: 'twitch', href: TWITCH_URL },
  { label: 'Bluesky', icon: 'bluesky', href: 'https://bsky.app/profile/fxbgames.bsky.social' },
  { label: 'Instagram', icon: 'instagram', href: 'https://www.instagram.com/fxbgames' },
  { label: 'Steam', icon: 'steam', href: steamStoreUrl('footer_social') },
  { label: 'GitHub', icon: 'github', href: 'https://github.com/chriskarpyszyn' },
  { label: 'YouTube', icon: 'youtube', href: 'https://www.youtube.com/@fxbgames' },
  { label: 'Discord', icon: 'discord', href: 'https://discord.gg/dK5HFBJ6af' },
];

const FEATURES = [
  { command: 'RACE THE CLOCK', output: 'Type 60 words against the timer and chase the #1 rank.' },
  { command: 'LEADERBOARDS', output: 'Compete globally and against your friends on every challenge.' },
  { command: 'STORY MODE', output: 'An empty ship. A patient AI. Keep typing.' },
  { command: 'KIDS MODE', output: 'Story-free practice for brand new typists.' },
  { command: 'LAUNCH DATE', output: `${RELEASE_DATE_LABEL}. Mark your star charts.` },
];

function WishlistButton({ className = '', label = 'Wishlist on Steam', utmContent = 'wishlist' }) {
  return (
    <a
      href={steamStoreUrl(utmContent)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 bg-ts-magenta hover:bg-ts-plum text-ts-cream font-bold rounded-md transition-all duration-200 hover:scale-105 active:scale-95 ${className}`}
    >
      <BrandIcon name="steam" className="w-5 h-5" />
      {label}
    </a>
  );
}


const COUNTDOWN_UNITS = [
  { key: 'days', label: 'DAYS' },
  { key: 'hours', label: 'HRS' },
  { key: 'minutes', label: 'MIN' },
  { key: 'seconds', label: 'SEC' },
];

function CountdownBoxes({ timeLeft }) {
  return (
    <div className="inline-flex items-stretch gap-2 sm:gap-3">
      {COUNTDOWN_UNITS.map((unit) => (
        <div
          key={unit.key}
          className="w-16 sm:w-20 rounded-lg border-2 border-ts-border bg-ts-crt/90 px-2 py-2.5 sm:py-3 text-center shadow-[0_4px_20px_rgba(22,40,38,0.35)]"
        >
          <span className="block font-pixel text-lg sm:text-2xl text-ts-cream tabular-nums">
            {formatCountdownUnit(timeLeft[unit.key])}
          </span>
          <span className="block font-mono text-[10px] sm:text-xs text-ts-sky mt-1 tracking-widest">
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function LateByBanner({ timeLeft }) {
  const dayLabel = timeLeft.days === 1 ? 'day' : 'days';

  return (
    <div
      className="inline-flex flex-col items-center gap-3"
      role="timer"
      aria-label={`We're late by ${timeLeft.days} ${dayLabel}, ${timeLeft.hours} hours, ${timeLeft.minutes} minutes, and ${timeLeft.seconds} seconds`}
    >
      <p className="font-pixel text-sm sm:text-base md:text-lg text-ts-cream leading-relaxed drop-shadow-[0_4px_0_rgba(22,40,38,0.85)]">
        We&apos;re late by{' '}
        <span className="text-ts-magenta">{timeLeft.days}</span>{' '}
        {dayLabel}
      </p>
      <p className="font-mono text-xs sm:text-sm text-ts-sand tabular-nums drop-shadow-[0_2px_4px_rgba(22,40,38,0.9)]">
        {timeLeft.hours}h {formatCountdownUnit(timeLeft.minutes)}m {formatCountdownUnit(timeLeft.seconds)}s and counting
      </p>
    </div>
  );
}

function Countdown() {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  useEffect(() => {
    const tick = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(tick);
  }, []);

  if (timeLeft.overdue) {
    return <LateByBanner timeLeft={timeLeft} />;
  }

  return (
    <div role="timer" aria-label={`Countdown to launch on ${RELEASE_DATE_LABEL}`}>
      <CountdownBoxes timeLeft={timeLeft} />
    </div>
  );
}

function Nav() {
  return (
    <nav className="sticky top-0 z-40 bg-ts-deep/80 backdrop-blur border-b border-ts-border">
      <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="font-pixel text-ts-cream text-[10px] sm:text-xs hover:text-ts-magenta transition-colors">
          FXB GAMES
        </Link>
        <div className="flex items-center gap-4 sm:gap-6">
          <a href="#about" className="font-mono text-sm text-ts-sand hover:text-ts-cream transition-colors">
            About
          </a>
          <a href="#press" className="hidden sm:block font-mono text-sm text-ts-sand hover:text-ts-cream transition-colors">
            Press
          </a>
          <a href="#twitch" className="hidden sm:block font-mono text-sm text-ts-sand hover:text-ts-cream transition-colors">
            Live Dev
          </a>
          <a href="#newsletter" className="hidden sm:block font-mono text-sm text-ts-sand hover:text-ts-cream transition-colors">
            Subscribe
          </a>
          <WishlistButton className="px-3 py-1.5 text-sm" label="Wishlist" utmContent="nav" />
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header className="relative">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-ts-deep/30 via-ts-deep/10 to-ts-bg"></div>
      </div>
      <div className="relative max-w-4xl mx-auto px-4 pt-44 sm:pt-64 md:pt-80 pb-10 text-center">
        <p className="inline-block bg-ts-deep/80 backdrop-blur-sm border border-ts-border rounded-full px-4 py-2 font-mono text-xs sm:text-sm text-ts-cream tracking-[0.25em] uppercase mb-4">
          A space typing game &middot; <span className="text-ts-magenta font-bold">launching {RELEASE_DATE_LABEL}</span>
        </p>
        <h1 className="font-pixel text-3xl sm:text-5xl md:text-6xl text-ts-cream leading-tight mb-6 drop-shadow-[0_4px_0_rgba(22,40,38,0.85)]">
          TYPING<br className="sm:hidden" /> STARS
        </h1>
        <p className="font-mono text-sm sm:text-base text-ts-cream/90 max-w-xl mx-auto mb-6 drop-shadow-[0_2px_4px_rgba(22,40,38,0.9)]">
          A relaxing typing game set in space. Race the clock, master your
          keyboard, and climb your way to the stars.
        </p>
        <div className="mb-8">
          <Countdown />
        </div>
        <div className="flex gap-3 justify-center flex-wrap">
          <WishlistButton className="px-8 py-4 text-base" utmContent="hero" />
          <a
            href={steamStoreUrl('hero_demo')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-ts-cream/70 hover:border-ts-cream hover:bg-ts-cream/10 text-ts-cream font-mono font-bold rounded-md transition-all duration-200 hover:scale-105 active:scale-95"
          >
            Play the Free Demo
          </a>
        </div>
      </div>
    </header>
  );
}

function TrailerModal({ onClose }) {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Typing Stars trailer"
    >
      <button
        type="button"
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(3, 7, 6, 0.88)',
          backdropFilter: 'blur(10px) saturate(70%)',
          WebkitBackdropFilter: 'blur(10px) saturate(70%)',
        }}
        onClick={onClose}
        aria-label="Close trailer"
      ></button>
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white/80 shadow-lg backdrop-blur-md transition-colors hover:border-white/40 hover:bg-black/80 hover:text-white sm:right-6 sm:top-6"
        aria-label="Close trailer"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      <div
        className="relative z-[1]"
        style={{ width: 'min(92vw, calc(88vh * 16 / 9), 1200px)' }}
      >
        <div className="rounded-2xl border border-ts-sky/30 bg-[#0a1110] p-1.5 shadow-[0_30px_100px_rgba(0,0,0,0.85),0_0_60px_rgba(127,176,168,0.08)] sm:p-2">
          <div className="relative aspect-video w-full overflow-hidden rounded-[10px] bg-black">
            {isPlaying ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${TRAILER_VIDEO_ID}?autoplay=1`}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                title="Typing Stars trailer"
              ></iframe>
            ) : (
              <button
                type="button"
                onClick={() => setIsPlaying(true)}
                className="group absolute inset-0 h-full w-full overflow-hidden text-ts-cream"
                aria-label="Play Typing Stars trailer"
              >
                <img
                  src={`https://i.ytimg.com/vi/${TRAILER_VIDEO_ID}/maxresdefault.jpg`}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
                <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10"></span>
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-black/65 shadow-2xl backdrop-blur-sm transition-all group-hover:scale-110 group-hover:bg-ts-magenta sm:h-20 sm:w-20">
                    <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 sm:h-9 sm:w-9" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </span>
                <span className="absolute bottom-4 left-4 rounded-md bg-black/65 px-3 py-2 font-mono text-xs font-bold backdrop-blur-sm sm:bottom-6 sm:left-6 sm:text-sm">
                  Watch the official trailer
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Screenshots() {
  const [showTrailer, setShowTrailer] = useState(false);

  return (
    <section className="max-w-5xl mx-auto px-4 -mt-2">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {SCREENSHOTS.map((shot) => (
          <a
            key={shot.src}
            href={steamStoreUrl('screenshots')}
            target="_blank"
            rel="noopener noreferrer"
            className="group block rounded-lg overflow-hidden border-2 border-ts-border hover:border-ts-magenta transition-colors"
          >
            <img
              src={shot.src}
              alt={shot.alt}
              loading="lazy"
              className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </a>
        ))}
      </div>
      <p className="text-center font-mono text-xs text-ts-sand mt-3">
        <button
          type="button"
          onClick={() => setShowTrailer(true)}
          className="hover:text-ts-cream transition-colors"
        >
          Watch the trailer →
        </button>
      </p>
      {showTrailer && <TrailerModal onClose={() => setShowTrailer(false)} />}
    </section>
  );
}

function About() {
  return (
    <section id="about" className="max-w-5xl mx-auto px-4 py-16 sm:py-24">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-pixel text-lg sm:text-xl text-ts-cream leading-relaxed mb-6">
            ALONE. IN SPACE.<br />
            WITH A <span className="text-ts-magenta">KEYBOARD.</span>
          </h2>
          <div className="font-mono text-sm sm:text-base text-ts-sand space-y-4">
            <p>
              You wake on an empty spaceship. There's a terminal in front of
              you, words on the screen, and an AI that would very much like you
              to keep typing.
            </p>
            <p>
              Typing Stars turns real typing practice into a game: every run
              sharpens your actual speed and accuracy while the story unfolds
              one cryptic message at a time.
            </p>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden border-2 border-ts-border shadow-[0_8px_40px_rgba(20,30,28,0.6)]">
          <div className="flex items-center gap-1.5 px-3 py-2 bg-ts-surface border-b border-ts-border">
            <span className="w-2.5 h-2.5 rounded-full bg-ts-magenta/70"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-ts-sand/70"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-ts-sky/70"></span>
            <span className="ml-2 font-mono text-xs text-ts-sand">typing_stars | terminal</span>
          </div>
          <div className="bg-ts-crt p-4 sm:p-6 font-mono text-xs sm:text-sm leading-relaxed">
            <p className="text-ts-sky mb-3">$ ./typing_stars --features</p>
            {FEATURES.map((feature) => (
              <p key={feature.command} className="mb-2">
                <span className="text-ts-magenta">&gt; {feature.command}</span>
                <br />
                <span className="text-ts-cream/80 pl-4">{feature.output}</span>
              </p>
            ))}
            <p className="text-ts-sky ts-caret mt-3">$ </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Press() {
  return (
    <section id="press" className="bg-ts-deep/60 border-y border-ts-border">
      <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20 text-center">
        <p className="font-pixel text-ts-magenta text-4xl mb-6" aria-hidden="true">&ldquo;</p>
        <blockquote className="font-mono text-base sm:text-lg text-ts-cream italic leading-relaxed mb-6">
          {PRESS.quote}
        </blockquote>
        <p className="font-mono text-sm text-ts-sand mb-2">
          <a
            href={PRESS.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ts-sky hover:text-ts-cream underline underline-offset-4 transition-colors"
          >
            {PRESS.source}
          </a>
        </p>
      </div>
    </section>
  );
}

function TwitchSection({ twitchStatus }) {
  const isLive = twitchStatus.isLive && !twitchStatus.loading;

  return (
    <section id="twitch" className="max-w-5xl mx-auto px-4 py-16 sm:py-24">
      <div className="rounded-lg border-2 border-ts-border bg-ts-surface/60 p-6 sm:p-10">
        {isLive ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-red-600 text-white font-mono font-bold text-sm px-4 py-2 rounded-full mb-4 animate-pulse">
              <span className="w-2.5 h-2.5 bg-white rounded-full"></span>
              LIVE NOW
            </div>
            <h2 className="font-pixel text-base sm:text-lg text-ts-cream mb-3">
              {twitchStatus.title || 'Streaming now!'}
            </h2>
            <p className="font-mono text-sm text-ts-sand mb-6">
              {twitchStatus.gameName && <>Playing {twitchStatus.gameName} &middot; </>}
              {twitchStatus.viewerCount?.toLocaleString() || 0} viewers watching
            </p>
            <div className="relative w-full rounded-lg overflow-hidden border-2 border-ts-border mb-6" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://player.twitch.tv/?channel=${TWITCH_CHANNEL}&parent=${window.location.hostname}&muted=false`}
                className="absolute top-0 left-0 w-full h-full"
                allowFullScreen
                title="Twitch Stream"
              ></iframe>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={TWITCH_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#9146FF] hover:bg-[#7c3ae0] text-white font-mono font-bold rounded-md transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <BrandIcon name="twitch" className="w-5 h-5" />
                Watch on Twitch
              </a>
              <Link
                to="/schedule"
                className="inline-flex items-center justify-center px-8 py-3 border-2 border-ts-border hover:border-ts-sand text-ts-sand hover:text-ts-cream font-mono text-sm rounded-md transition-colors text-center"
              >
                Stream schedule
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <h2 className="font-pixel text-lg sm:text-xl text-ts-cream leading-relaxed mb-4">
                BUILT <span className="text-[#a970ff]">LIVE</span> ON TWITCH
              </h2>
              <p className="font-mono text-sm sm:text-base text-ts-sand max-w-xl">
                Typing Stars is being developed almost entirely on stream.
                Follow itsFlannelBeard to watch new features come to life,
                suggest ideas in chat, and hang out while the game gets made.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href={TWITCH_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#9146FF] hover:bg-[#7c3ae0] text-white font-mono font-bold rounded-md transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <BrandIcon name="twitch" className="w-5 h-5" />
                Follow on Twitch
              </a>
              <Link
                to="/schedule"
                className="inline-flex items-center justify-center px-8 py-3 border-2 border-ts-border hover:border-ts-sand text-ts-sand hover:text-ts-cream font-mono text-sm rounded-md transition-colors text-center"
              >
                Stream schedule
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function WishlistBand() {
  return (
    <section id="wishlist" className="bg-ts-deep/60 border-y border-ts-border">
      <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20 text-center">
        <h2 className="font-pixel text-base sm:text-lg text-ts-cream leading-relaxed mb-3">
          LAUNCHING <span className="text-ts-magenta">{RELEASE_DATE_LABEL.toUpperCase()}</span>
        </h2>
        <p className="font-mono text-sm text-ts-sand mb-8">
          Wishlist Typing Stars on Steam to get notified at launch. The free
          demo is available now.
        </p>
        <iframe
          src={steamWidgetUrl('wishlist_band')}
          frameBorder="0"
          className="w-full max-w-xl mx-auto rounded-lg"
          style={{ height: '190px' }}
          title="Typing Stars on Steam"
        ></iframe>
      </div>
    </section>
  );
}

function MailingListSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(LOOPS_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email,
          userGroup: LOOPS_USER_GROUP,
          mailingLists: LOOPS_MAILING_LIST_ID,
        }).toString(),
      });

      if (response.status === 429) {
        setStatus('error');
        setErrorMessage('Too many signups right now — please try again in a minute.');
        return;
      }

      const data = await response.json();
      if (response.ok && data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      // Loops' rate limiter can also surface as a failed fetch.
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again in a minute.');
    }
  };

  return (
    <section id="newsletter" className="max-w-5xl mx-auto px-4 py-16 sm:py-24">
      <div className="max-w-xl mx-auto text-center">
        <h2 className="font-pixel text-base sm:text-lg text-ts-cream leading-relaxed mb-3">
          GET <span className="text-ts-magenta">STUDIO NEWS</span>
        </h2>
        <p className="font-mono text-sm text-ts-sand mb-8">
          Launch news and dev updates from fxb games, straight to your inbox.
          No spam — unsubscribe anytime.
        </p>
        <div className="rounded-lg overflow-hidden border-2 border-ts-border text-left shadow-[0_8px_40px_rgba(20,30,28,0.6)]">
          <div className="flex items-center gap-1.5 px-3 py-2 bg-ts-surface border-b border-ts-border">
            <span className="w-2.5 h-2.5 rounded-full bg-ts-magenta/70"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-ts-sand/70"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-ts-sky/70"></span>
            <span className="ml-2 font-mono text-xs text-ts-sand">studio_news — mail</span>
          </div>
          <div className="bg-ts-crt p-4 sm:p-6 font-mono text-xs sm:text-sm leading-relaxed">
            <p className="text-ts-sky mb-3">$ subscribe --studio-news</p>
            {status === 'success' ? (
              <>
                <p className="text-ts-cream/80 mb-2">
                  <span className="text-ts-magenta">&gt; OK.</span> You're on the list. See you among the stars.
                </p>
                <p className="text-ts-sky ts-caret">$ </p>
              </>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    aria-label="Email address"
                    disabled={status === 'loading'}
                    className="flex-1 min-w-0 bg-ts-deep/60 border-2 border-ts-border focus:border-ts-sky focus:outline-none rounded-md px-4 py-3 font-mono text-sm text-ts-cream placeholder-ts-sand/50 disabled:opacity-60 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="inline-flex items-center justify-center px-6 py-3 bg-ts-magenta hover:bg-ts-plum text-ts-cream font-mono font-bold text-sm rounded-md transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-60 disabled:hover:scale-100 whitespace-nowrap"
                  >
                    {status === 'loading' ? 'Sending…' : 'Sign Up'}
                  </button>
                </form>
                {status === 'error' && (
                  <p className="text-ts-magenta mt-3" role="alert">
                    &gt; ERROR: {errorMessage}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="max-w-5xl mx-auto px-4 py-12 text-center">
      <div className="flex justify-center gap-5 mb-4">
        {SOCIAL_LINKS.filter((social) => social.href).map((social) => (
          <a
            key={social.label}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={social.label}
            title={social.label}
            className="text-ts-sand hover:text-ts-magenta transition-colors hover:scale-110 transform duration-200"
          >
            <BrandIcon name={social.icon} className="w-6 h-6" />
          </a>
        ))}
      </div>
      <a
        href={`mailto:${CONTACT_EMAIL}`}
        className="inline-block font-mono text-sm text-ts-sand hover:text-ts-magenta transition-colors mb-8"
      >
        {CONTACT_EMAIL}
      </a>
      <p className="font-mono text-xs text-ts-sand/60">
        © {new Date().getFullYear()} fxb games studio
      </p>
    </footer>
  );
}

export default function Home() {
  const twitchStatus = useTwitchStatus(TWITCH_CHANNEL);

  return (
    <div className="min-h-screen bg-ts-bg text-ts-cream relative">
      <div className="ts-stars absolute inset-0 pointer-events-none" aria-hidden="true"></div>
      <div className="relative">
        <Nav />
        <Hero />
        <Screenshots />
        <About />
        <Press />
        <TwitchSection twitchStatus={twitchStatus} />
        <WishlistBand />
        <MailingListSection />
        <Footer />
      </div>
    </div>
  );
}
